/**
 * A pe3d release in the browser: what the preview needs to show any variant
 * without a server round trip per edit.
 *
 * The release ships its own `variants.mjs` — the engine's src/variants built
 * for the browser, the same code its templates were built with — so the
 * preview creative is made exactly as the server's export makes it: the base
 * pack is unpacked once, a variant's asset overrides are packed over it, and
 * the pack, the variant JSON and the loading screen are injected into the
 * preview network's template. A config-only edit reuses the last pack, so
 * typing in a field costs an inject (a string replace), not a repack.
 *
 * Everything a release serves is immutable, so one load per game@version is
 * kept for the session.
 */

import { fetchReleaseBundle, fetchReleaseFile, fetchSlotSource } from "./pe3dApi";

const releases = new Map();

/** Load a module from source text: a blob URL, imported outside webpack. */
async function importModuleText(text) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/javascript" }));
  try {
    return await import(/* webpackIgnore: true */ url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function load(game, version) {
  const opened = await fetchReleaseBundle(game, version);
  if (!opened.ok) throw new Error(typeof opened.error === "string" ? opened.error : JSON.stringify(opened.error));
  const bundle = opened.data.bundle;
  const previewInfo = bundle.networks[bundle.preview];
  const liveFiles = bundle.live ? bundle.live.files : [];
  const [code, packText, ...texts] = await Promise.all([
    fetchReleaseFile(game, version, bundle.variantCode.browser).then(importModuleText),
    fetchReleaseFile(game, version, bundle.pack),
    ...previewInfo.files.map((name) => fetchReleaseFile(game, version, `templates/${bundle.preview}/${name}`)),
    ...liveFiles.map((name) => fetchReleaseFile(game, version, `templates/_live/${name}`)),
  ]);
  const templates = Object.fromEntries(previewInfo.files.map((name, i) => [name, texts[i]]));
  const liveTemplates = Object.fromEntries(liveFiles.map((name, i) => [name, texts[previewInfo.files.length + i]]));
  return createRelease({ game, version, bundle, code, packText, templates, liveTemplates });
}

const IMAGE_FILE = /\.(png|jpe?g|webp|gif)$/i;
const mimeOf = (file) => {
  const ext = String(file).split(".").pop().toLowerCase();
  return `image/${ext === "jpg" ? "jpeg" : ext}`;
};

const FILE_MIME = { mp3: "audio/mpeg", m4a: "audio/mp4", aac: "audio/aac", ogg: "audio/ogg", wav: "audio/wav", glb: "model/gltf-binary" };
const fileMimeOf = (file) => {
  const ext = String(file || "").split(".").pop().toLowerCase();
  return FILE_MIME[ext] || (IMAGE_FILE.test(String(file)) ? mimeOf(file) : "application/octet-stream");
};

/** base64 → bytes. */
function bytesOf(base64) {
  const binary = atob(base64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

/**
 * The release object, separate from loading so it can be tested with a fake
 * `code`. `fetchSource(slot, ref)` answers like pe3d/release/slotSource: the
 * server reads release and media library files alike and names the companion
 * files the packer will ask for, so the browser does no lookups of its own.
 */
export function createRelease({ game, version, bundle, code, packText, templates, liveTemplates = {}, fetchSource }) {
  const base = code.unpackAssets(packText);
  const slotSources = new Map();
  const ownThumbs = new Map();
  const fileUrls = new Map();
  let lastPack = { key: null, result: null };

  const sourceFor = (slot, ref) => {
    const key = `${slot}:${JSON.stringify(ref)}`;
    if (!slotSources.has(key)) {
      const load = (fetchSource || ((s, r) => fetchSlotSource(game, version, s, r).then((result) => {
        if (!result.ok) throw new Error(typeof result.error === "string" ? result.error : JSON.stringify(result.error));
        return result.data;
      })))(slot, ref);
      slotSources.set(key, load);
      load.catch(() => slotSources.delete(key));
    }
    return slotSources.get(key);
  };

  /** What the server says about a slot's file (size, triangles, clips, problems), for the cards. */
  const describeSlot = (slot, ref) => sourceFor(slot, ref).then(({ file, meta, errors }) => ({ file, meta, errors }));

  /** The game's own file of a slot, as packed: { file, bytes } (a byte count), or null. */
  const ownFileOf = (slot) => {
    const own = base.sources && base.sources[slot];
    return own ? { file: own.file, bytes: own.bytes ? own.bytes.length : 0 } : null;
  };

  /**
   * A slot's file as a URL the browser can play or load (a blob: URL, kept
   * for the session): the sound for the card's play button, the model for
   * the inspector. No ref is the game's own file, from the base pack.
   * Resolves to { url, file } or null.
   */
  async function slotFileUrl(slot, ref) {
    const key = `${slot}:${JSON.stringify(ref || null)}`;
    if (!fileUrls.has(key)) {
      const loading = (async () => {
        if (!ref) {
          const own = base.sources && base.sources[slot];
          if (!own || !own.bytes) return null;
          return { url: URL.createObjectURL(new Blob([own.bytes], { type: fileMimeOf(own.file) })), file: own.file };
        }
        const loaded = await sourceFor(slot, ref);
        if (!loaded.data) return null;
        return { url: URL.createObjectURL(new Blob([bytesOf(loaded.data)], { type: fileMimeOf(loaded.file) })), file: loaded.file };
      })();
      fileUrls.set(key, loading);
      loading.catch(() => fileUrls.delete(key));
    }
    return fileUrls.get(key);
  }

  /**
   * A picture of a slot's file for its card, or null: the file itself when it
   * is an image, else a pair's page image. No ref is the game's own file,
   * which is already here in the base pack.
   */
  async function slotThumbnail(slot, ref) {
    if (!ref) {
      const own = base.sources && base.sources[slot];
      if (!own || !IMAGE_FILE.test(own.file || "") || !own.bytes) return null;
      if (!ownThumbs.has(slot)) ownThumbs.set(slot, URL.createObjectURL(new Blob([own.bytes], { type: mimeOf(own.file) })));
      return ownThumbs.get(slot);
    }
    const loaded = await sourceFor(slot, ref);
    if (loaded.data && IMAGE_FILE.test(loaded.file || "")) return `data:${mimeOf(loaded.file)};base64,${loaded.data}`;
    const page = Object.entries(loaded.parts || {}).find(([name]) => IMAGE_FILE.test(name));
    return page ? `data:${mimeOf(page[0])};base64,${page[1]}` : null;
  }

  /** The variant's pack: the base pack with its asset overrides packed over it. */
  async function packFor(assets) {
    const key = JSON.stringify(assets || {});
    if (lastPack.key === key) return lastPack.result;
    const sources = { ...base.sources };
    const problems = [];
    for (const [slot, ref] of Object.entries(assets || {})) {
      let loaded;
      try {
        loaded = await sourceFor(slot, ref);
      } catch (err) {
        problems.push(`assets.${slot}: ${err.message}`);
        continue;
      }
      if (!loaded.data) {
        problems.push(...(loaded.errors || [`assets.${slot}: unreadable`]));
        continue;
      }
      const parts = Object.fromEntries(Object.entries(loaded.parts || {}).map(([name, data]) => [name, bytesOf(data)]));
      sources[slot] = { file: loaded.file, bytes: bytesOf(loaded.data), readPart: (name) => parts[name] || null };
    }
    const pack = code.packAssets(bundle.schema, sources);
    const result = { pack, problems: [...problems, ...pack.errors] };
    lastPack = { key, result };
    return result;
  }

  /**
   * The preview creative for a variant: { html, bytes, problems }. With
   * problems the html is still made from what is valid, so the frame keeps
   * showing something while the user fixes a field.
   */
  async function previewHtml(variant, loadingScreen = "blankLoading") {
    const { pack, problems: packProblems } = await packFor(variant.assets);
    const problems = [...code.validateVariant(bundle.schema, variant), ...packProblems];
    const payload = {
      variantJson: code.variantJson(variant),
      packBase64: pack.base64,
      loadingHtml: bundle.loadingScreens[loadingScreen] || bundle.loadingScreens.blankLoading,
    };
    const info = bundle.networks[bundle.preview];
    const html = code.injectVariant(templates[info.html], payload);
    const others = info.files.filter((name) => name !== info.html).reduce((sum, name) => sum + templates[name].length, 0);
    const bytes = new Blob([html]).size + others;
    return { html, bytes, maxBytes: bundle.maxBytes || 5000000, problems };
  }

  /**
   * The live preview's starting page: the QA-bridge build of the preview
   * network with the variant injected (null when the release has none). After
   * this, edits reach the running game over the bridge (liveFiles) instead of a
   * reload.
   */
  async function liveHtml(variant, loadingScreen = "blankLoading") {
    if (!bundle.live) return null;
    const { pack } = await packFor(variant.assets);
    return code.injectVariant(liveTemplates[bundle.live.html], {
      variantJson: code.variantJson(variant),
      packBase64: pack.base64,
      loadingHtml: bundle.loadingScreens[loadingScreen] || bundle.loadingScreens.blankLoading,
    });
  }

  const nameOf = (file) => String(file || "").slice(String(file || "").lastIndexOf("/") + 1);
  const bufferOf = (bytes) => bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);

  /**
   * A slot's files as the bridge's `set-asset` takes them, `{ name: ArrayBuffer }`
   * — the descriptor under its own name and each companion under the name the
   * descriptor uses. `ref` null means the game's own file, read from the base pack.
   */
  async function liveFiles(slot, ref) {
    if (!ref) {
      const source = base.sources[slot];
      const packed = base.slots[slot] || {};
      const files = { [nameOf(source.file)]: bufferOf(source.bytes) };
      for (const name of Object.keys(packed.parts || {})) {
        const part = source.readPart(name);
        if (part) files[name] = bufferOf(part);
      }
      return files;
    }
    const loaded = await sourceFor(slot, ref);
    if (!loaded.data) throw new Error((loaded.errors || [`assets.${slot}: unreadable`]).join("; "));
    const files = { [nameOf(loaded.file)]: bufferOf(bytesOf(loaded.data)) };
    for (const [name, data] of Object.entries(loaded.parts || {})) files[name] = bufferOf(bytesOf(data));
    return files;
  }

  return { game, version, bundle, code, previewHtml, describeSlot, ownFileOf, slotThumbnail, slotFileUrl, liveHtml, liveFiles, hasLive: Boolean(bundle.live) };
}

/** The release for game@version, loaded once per session. */
export function openRelease(game, version) {
  const key = `${game}@${version}`;
  if (!releases.has(key)) {
    const loading = load(game, version);
    releases.set(key, loading);
    loading.catch(() => releases.delete(key));
  }
  return releases.get(key);
}
